<template>
  <div :class="['profile-header', { 'profile-header--preview': isCVPreview }]" v-if="userContactInfo">
    <div class="profile-header__background">
      <profile-image :userId="userId" />
    </div>

    <div class="profile-header__top">
      
      <el-avatar class="profile-header__avatar" :size="isCVPreview ? 54 : 120">
        <user-avatar :userId="userId" :alt="altAvatar" />
      </el-avatar>

      <div class="profile-header__top-content">
        <div class="profile-header__top-info">
          <h1 class="profile-header__name">
            {{ userContactInfo.firstName }} {{ userContactInfo.lastName }}
          </h1>
          <span class="profile-header__birthday" v-if="isCurrentUser && !isPublicCV">
            {{ formatDate(userContactInfo.birthday, DEFAULT_DATE_FORMAT) }}
          </span>
          <span class="profile-header__separator">&middot;</span>

          <span class="profile-header__gender" v-if="userContactInfo.gender">
            {{ GENDER[userContactInfo.gender] }}
          </span>
          <span v-if="userContactInfo.demo" class="profile-header__separator">&middot;</span>
          <el-tag v-if="userContactInfo.demo" type="primary">
            Demo
          </el-tag>

          <span class="profile-header__website" v-if="userContactInfo.webSite">
            &middot;
            <a :href="userContactInfo.webSite" target="_blank">
              <el-icon class="profile-header__website-icon">
                <HomeFilled />
              </el-icon>  
              {{ userContactInfo.webSite }}
            </a>
          </span>
        </div>

        <div class="profile-header__buttons">
          <el-button type="primary" @click="startEditing" v-if="isCurrentUser && !isPublicCV && currentUser.userId">
            Edit
          </el-button>

          <el-button type="primary" plain @click="showPublicCV"
            v-if="isCurrentUser && !isPublicCV && currentUser.userId">
            Show my public CV
          </el-button>

          <el-dropdown v-if="userContactInfo.userConnectStatus == 'received_pending'">
            <el-button type="primary">
              Respond to request <el-icon><arrow-down /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="respondToConnectRequest(true)">Accept</el-dropdown-item>
                <el-dropdown-item @click="respondToConnectRequest(false)">Decline</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-dropdown v-if="userContactInfo.userConnectStatus == 'connected'">
            <el-button type="primary">
              Connected <el-icon><arrow-down /></el-icon>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="showDisconnectDialog = true">
                  Disconnect?
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <el-button type="primary" @click="connectButtonClicked"
            v-if="!isCurrentUser && currentUser.userId && userContactInfo.userConnectStatus != 'received_pending' && userContactInfo.userConnectStatus != 'connected'"
            :disabled="!enableConnectRequestButton">
            {{ connectLabel }}
          </el-button>

          <el-button type="primary" @click="isContactUserFormVisible = true"
            v-if="!isCurrentUser && currentUser.userId">
            Send message
          </el-button>
        </div>

        <el-button type="primary" plain @click="returnToMyProfile" v-if="isPublicCV">Return to my profile</el-button>
      </div>
    </div>

    <div class="profile-header__about-user" v-if="userContactInfo.extraCV">
      <div class="profile-header__about-user-label">
        About me:
      </div>
      {{ userContactInfo.extraCV }}
    </div>

    <div v-if="userContactInfo?.userSocialNetworks?.length > 0" class="profile-header__userSocialNetworks">
      <span>
      <a class="profile-header__socials" v-for="(social, index) in userContactInfo?.userSocialNetworks" :key="index"
          :href="social.socialNetworkBaseUrl + social.accountLink" target="_blank">
          <el-avatar :size="32" >
            <el-image :src="social.iconURL" :key="social.name" alt="social.name" :size="32"/>
          </el-avatar>
      </a>
      </span>
    </div>

    <div class="profile-header__phone" v-if="userContactInfo.phone">
      <icon class="profile-header__phone-icon" :width="20" :height="20" glyph="phone" />
      {{ userContactInfo.phone }}
    </div>

    <el-dialog v-model="showDisconnectDialog" width="30%" title="Confirmation" v-if="!isCurrentUser">
      <span>Do you really want to disconnect?</span>
      <template #footer>
        <el-button @click="showDisconnectDialog = false">Cancel</el-button>
        <el-button type="primary" @click="disconnectFromUser">Confirm</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="isContactUserFormVisible" width="40%" title="Contact user" v-if="!isCurrentUser">
      <el-form :rules="contactUserFormRules" :model="contactUserForm" ref="contactUserForm">
        <el-form-item prop="text" :error="contactUserFormErrors.text" label="Message">
          <el-input v-model="contactUserForm.text" type="textarea" :rows="4">
          </el-input>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="isContactUserFormVisible = false">Cancel</el-button>
        <el-button type="primary" @click="validateContactUserForm" :disabled="isButtonDisabled('send-message')">
          Send message
        </el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="isConnectUserFormVisible" width="40%" title="Connect with user" v-if="!isCurrentUser">
      <el-form :rules="connectUserFormRules" :model="connectUserForm" ref="connectUserForm">
        <el-form-item prop="text" :error="connectUserFormErrors.text" label="Message">
          <el-input v-model="connectUserForm.text" type="textarea" :rows="4">
          </el-input>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="isConnectUserFormVisible = false">Cancel</el-button>
        <el-button type="primary" @click="validateConnectUserForm" :disabled="isButtonDisabled('send-request')">
          Send request
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>


<script>
import Icon from '@/components/common/Icon.vue';
import { sendMessageToUser, sendConnectionRequestToUser, respondToConnectRequest, revokeConnection } from '@/api/profile.js';
import { DEFAULT_DATE_FORMAT, formatDate } from '@/utils/index.js';
import { showMessage } from '@/utils/messages.js';
import { GENDER } from '@/enum/gender.js';
import { disableButton } from '@/mixins/disableButton.js';
import UserAvatar from '@/components/profile/UserAvatar.vue'
import ProfileImage from '@/components/profile/ProfileImage.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue';


export default {

  name: 'ProfileHeader',
  props: {
    isCurrentUser: {
      type: Boolean,
      required: false,
    },
    userId: {
      type: String,
      required: true,
    },
    userContactInfo: {
      type: Object,
      required: false,
    },
    isCVPreview: {
      type: Boolean,
      required: false,
    },
  },
  mixins: [disableButton],
  components: {
    Icon,
    UserAvatar,
    ProfileImage,
    ConfirmationDialog,
  },

  data() {
    return {
      isPublicCV: false,
      DEFAULT_DATE_FORMAT,
      GENDER,

      altAvatar: "User avatar",

      isContactUserFormVisible: false,
      isConnectUserFormVisible: false,
      showDisconnectDialog: false,
      contactUserFormRules: {
        text: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter message',
          },
        ],
      },
      contactUserForm: {
        text: null,
      },
      contactUserFormErrors: {
        text: null,
      },
      connectUserFormRules: {
        text: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter message',
          },
        ],
      },
      connectUserForm: {
        text: null,
      },
      connectUserFormErrors: {
        text: null,
      },
    };
  },

  computed: {
    userAddress() {
      const { userContactInfo } = this;

      const address =
        (userContactInfo.city ? userContactInfo.city + ', ' : '') +
        (userContactInfo.country ? userContactInfo.country : '');

      return address;
    },
    currentUser() {
      return this.$store.state.user;
    },
    enableConnectRequestButton() {
      //console.log('canSendConnectRequest', this.userContactInfo.userConnectStatus);
      if (this.userContactInfo.userConnectStatus === undefined || this.userContactInfo.userConnectStatus == null) {
        return true;
      }
      switch (this.userContactInfo.userConnectStatus) {
        case "na":
        case "initiated_pending":
        case "connected":
        case "received_blocked":
          return false;
        default:
          return true;
      }
    },
    connectLabel() {
      //console.log('connectLabel', this.userContactInfo.userConnectStatus);
      if (this.userContactInfo.userConnectStatus === undefined || this.userContactInfo.userConnectStatus == null) {
        return 'Connect';
      }
      switch (this.userContactInfo.userConnectStatus) {
        case "received_blocked":
        case "na":
          return 'N/A';
        case "received_pending":
          return 'Connection pending';
        case "initiated_pending":
          return 'Connection requested';
        case "initiated_blocked":
          return 'Unblock';
        case "connected":
          return 'Connected';
        default:
          return 'Connect';
      }
    }
  },
  methods: {
    formatDate,
    showMessage,
    async requestSendMessageToUser() {
      this.disableButton('send-message');

      const { userId } = this;
      const { text } = this.contactUserForm;
      const { success, errors } = await sendMessageToUser({ userId, text });
      this.enableButton('send-message');

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Your message has been sent',
        });
        this.isContactUserFormVisible = false;
        this.contactUserForm.text = null;
        this.contactUserFormErrors.text = null;
      }
    },
    async requestSendConnectionRequestToUser() {
      this.disableButton('send-request');
      //this.disableButton('connect');

      const { userId } = this;
      const { text } = this.connectUserForm;

      const { success, errors } = await sendConnectionRequestToUser({ userId, text });

      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Your request has been sent',
        });
        this.isConnectUserFormVisible = false;
        this.connectUserForm.text = null;
        this.connectUserFormErrors.text = null;
        this.userContactInfo.userConnectStatus = 'initiated_pending';
      }
      else {
        this.enableButton('send-request');
        this.enableButton('connect');
      }
    },
    convertToLowerCase(data) {
      return data.toLowerCase();
    },
    showPublicCV() {
      const { isCurrentUser } = this;

      if (isCurrentUser) {
        this.isPublicCV = true;
        this.$emit('toggle-public-cv', true);
      }
    },
    returnToMyProfile() {
      this.isPublicCV = false;
      this.$emit('toggle-public-cv', false);
    },
    startEditing() {
      const { userId } = this;

      this.$router.push({
        name: 'UserProfile',
        params: {
          link: userId,
          tab: 'personal-data',
        },
        query: {
          edit: true,
        },
      });
    },
    validateContactUserForm() {
      const { text } = this.contactUserForm;

      if (this.$refs.contactUserForm) {
        this.$refs.contactUserForm.validate();
      }

      if (text && text.trim()) {
        this.requestSendMessageToUser();
      }
    },
    validateConnectUserForm() {
      const { text } = this.connectUserForm;

      if (this.$refs.connectUserForm) {
        this.$refs.connectUserForm.validate();
      }

      if (text && text.trim()) {
        this.requestSendConnectionRequestToUser();
      }
    },
    connectButtonClicked() {
      //console.log('connectButtonClicked', this.userContactInfo.userConnectStatus);
      switch (this.userContactInfo.userConnectStatus) {
        case "received_pending":
          break;
        default:
          this.isConnectUserFormVisible = true;
          break;
      }
    },
    async respondToConnectRequest(accept) {
      //console.log('respondToConnectRequest', accept);
      const { userId } = this;
      const { success, errors } = await respondToConnectRequest({ userId, accept });
      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Your response has been sent',
        });
        this.userContactInfo.userConnectStatus = accept ? 'connected' : 'initiated_declined';
      }
    },
    async disconnectFromUser() {
      //console.log('disconnectFromUser');
      const { userId } = this;
      const { success, errors } = await revokeConnection({ userId});
      if (success) {
        this.showMessage({
          type: 'success',
          message: 'Connection revoked',
        });
        this.userContactInfo.userConnectStatus = 'initiated_revoked';
      }
      this.showDisconnectDialog = false;
    },
  },
};
</script>

<style lang="scss">
@import '~@/styles/shared/_globals.scss';

.profile-header {
  $this: &;

  overflow: hidden;
  padding: 0 var(--global-dimension-size-300) var(--global-dimension-size-400);
  background-color: var(--color-gray-50);
  border-radius: var(--border-radius-base);
  margin-bottom: 30px;

  &__background {
    position: relative;
    min-width: 100%;
    height: 200px;
    margin-left: -24px; // --global-dimension-size-300
    margin-right: -24px;

    img {
      position: absolute;
      width: 100%;
      height: 100%;

      object-fit: cover;
      object-position: center;
    }
  }

  &__top {
    position: relative;
    display: flex;
    flex-flow: row wrap;
    align-items: flex-end;

    margin-top: -42px;
    margin-bottom: var(--global-dimension-size-300);

    @include from('sm') {
      flex-wrap: nowrap;
    }
  }

  &__avatar {
    margin-right: var(--global-dimension-size-300);
  }

  &__top-content {
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
    align-items: flex-start;
    flex-grow: 1;

    min-height: 60px;
    padding-top: var(--global-dimension-size-250);

    @include from('sm') {
      flex-wrap: nowrap;
    }
  }

  &__top-info {}

  &__name {
    margin: 0;
    font-weight: normal;
    font-size: 26px;
    line-height: 1.5;
  }

  &__birthday {
    font-size: var(--font-size-200);
    line-height: 1.5;
    margin-right: var(--global-dimension-size-125);
  }

  &__gender {
    font-size: var(--font-size-200);
    line-height: 1.5;
    color: var(--color-gray-700);
  }

  &__separator {
      margin: 0 var(--global-dimension-size-50);
  }

  &__demo {
    margin-right: var(--global-dimension-size-200);
    margin-bottom: var(--global-dimension-size-200);
  }

  &__buttons {}

  &__about-user {
    margin-bottom: var(--global-dimension-size-200);
    font-size: 16px;
    line-height: 1.5;
  }

  &__phone {
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: var(--global-dimension-size-300);
  }

  &__phone-icon {
    margin-right: var(--global-dimension-size-75);
  }

  &__userSocialNetworks {
    margin-bottom: var(--global-dimension-size-250);

    @include from('lg') {
      margin-bottom: 0;
    }
  }

  &__social {
    margin-bottom: var(--global-dimension-size-150);
    color: var(--color-gray-900);
    font-size: var(--font-size-200);
    box-align: center;
  }

  &__social-link {
    display: flex;
    align-items: center;

    &:hover {
      color: var(--color-primary-400);
    }
  }

  &__social-name {
    font-weight: var(--global-font-weight-bold);
    margin-right: var(--global-dimension-size-75);
  }

  &__social-address {
    display: inline-block;
  }

  &__facts {}

  &__fact {
    display: flex;
    align-items: center;
    margin-bottom: var(--global-dimension-size-150);
    font-size: var(--font-size-200);
  }

  &__fact-icon {
    margin-right: var(--global-dimension-size-100);
  }

  &--preview {
    #{$this}__background {
      display: none;
    }

    #{$this}__avatar {
      margin-right: var(--global-dimension-size-125);
    }

    #{$this}__top {
      align-items: center;
      margin-top: 0;
      padding-top: var(--global-dimension-size-350);
    }

    #{$this}__top-content {
      padding-top: 0;
    }

    #{$this}__name {
      font-size: var(--font-size-500);
    }
  }
}
</style>
